import { makeAutoObservable } from 'mobx';
import type { BaseRuleListItem } from '../../rules';
import dayjs from 'dayjs';

const initialGlobalUpdateState = {
  allSelected: false,
  count: 0,
  indeterminate: false,
};

class SelectedRulesStore {
  renamingCount = 0;
  globalUpdate = { ...initialGlobalUpdateState };
  private store: BaseRuleListItem[] = [];
  private originalRules: BaseRuleListItem[] = [];

  constructor() {
    makeAutoObservable(this);
  }

  get rules() {
    return this.store;
  }

  get renamed() {
    return this.renamingCount > 0;
  }

  selectAll(rules: BaseRuleListItem[], count: number) {
    this.globalUpdate = { allSelected: true, count: count, indeterminate: false };
    rules.forEach((r) => {
      this.addRule(r);
    });
  }

  isRuleSelected(id: number) {
    return !!this.store.find((r) => r.id === id);
  }

  removeRule(id: number) {
    this.store = this.store.filter((r) => r.id !== id);
    this.originalRules = this.originalRules.filter((r) => r.id !== id);
  }

  addRule(r: BaseRuleListItem) {
    this.store.push(r);
    this.originalRules.push(r);
  }

  renameRules(searchValue: string, replaceValue: string) {
    this.store = this.store.map((r) => {
      const renamed = r.name.replace(searchValue, replaceValue);
      return { ...r, name: renamed };
    });

    this.increaseCount();
  }

  addPostFix(postfix = `duplicated_${dayjs().format('DD_MM_YYYY')}`) {
    this.store.forEach((r) => {
      r.name += `_${postfix}`;
    });

    this.increaseCount();
  }

  toggleRule(r: BaseRuleListItem) {
    if (this.isRuleSelected(r.id)) {
      if (this.globalUpdate.allSelected) {
        this.globalUpdate = { ...this.globalUpdate, allSelected: false, indeterminate: true };
      }
      this.removeRule(r.id);
    } else {
      this.addRule(r);
    }
  }

  checkIfRuleWasRenamed(id: number) {
    const oldRule = this.originalRules.find((r) => r.id === id);
    const newRule = this.store.find((r) => r.id === id);

    if (!oldRule || !newRule) return false;
    return oldRule.name !== newRule.name;
  }

  checkIfAllRulesWereRenamed() {
    return this.rules.every((r) => this.checkIfRuleWasRenamed(r.id));
  }

  reset() {
    this.store = [];
    this.originalRules = [];
    this.renamingCount = 0;
    this.globalUpdate = { ...initialGlobalUpdateState };
  }

  private increaseCount() {
    this.renamingCount += 1;
  }
}

export const selectedRulesStore = new SelectedRulesStore();
